import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import { isInstantPayEnabledUsdMarket } from '@/enterprise/data/instantPayEnrollment';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';

export type CashFlowChartView = 'awardedAp' | 'totalEligibleAp';

export interface CashFlowViewMetrics {
  earlyPayCashFlow: number;
  additionalCashImpact: number;
  dsoPercent: number;
  dsoDays: number;
}

export interface CashFlowQuarterDataPoint {
  yearQuarter: string;
  months: string[];
  cashBenefit: number;
  dsoPercent: number;
}

type TotalInstantPayImpactResponse =
  paths['/instant-pay-impact/total']['get']['responses']['200']['content']['application/json'];
type QuarterlyInstantPayImpactResponse =
  paths['/instant-pay-impact/quarterly']['get']['responses']['200']['content']['application/json'];

const EMPTY_VIEW_METRICS: CashFlowViewMetrics = {
  earlyPayCashFlow: 0,
  additionalCashImpact: 0,
  dsoPercent: 0,
  dsoDays: 0,
};

const useInstantPayMakerOrganizationUuids = (): string[] =>
  useTakerMarkets((takerMarkets) => [...new Set(takerMarkets.map((market) => market.makerOrganizationUuid))]).data;

// Sum USD invoice totals for enabled Instant Pay divisions on USD markets.
export const useInstantPayAvailableToday = (): number =>
  useTakerMarkets((takerMarkets) =>
    takerMarkets
      .filter((market) => isInstantPayEnabledUsdMarket(market.instantPay, market.currency))
      .reduce((sum, market) => sum + market.totalInvoiceAmountUsd, 0)
  ).data;

const buildInstantPayImpactSearchParams = (makerOrganizationUuids: string[]) =>
  qs.stringify({ makerOrganizationUuids }, { arrayFormat: 'repeat' });

export const useInstantPayCashFlowTotal = (): Record<CashFlowChartView, CashFlowViewMetrics> => {
  const makerOrganizationUuids = useInstantPayMakerOrganizationUuids();
  const isValid = makerOrganizationUuids.length > 0;

  const { data } = useSuspenseQuery({
    queryKey: isValid ? ['instant-pay-impact-total', ...makerOrganizationUuids] : ['instant-pay-impact-total-invalid'],
    queryFn: () =>
      isValid
        ? apiClient
            .get('api/supplier-experience/instant-pay-impact/total', {
              searchParams: buildInstantPayImpactSearchParams(makerOrganizationUuids),
            })
            .json<TotalInstantPayImpactResponse>()
        : null,
    select: (response) => response?.data ?? null,
  });

  if (!data) {
    return { awardedAp: EMPTY_VIEW_METRICS, totalEligibleAp: EMPTY_VIEW_METRICS };
  }

  return {
    awardedAp: {
      earlyPayCashFlow: data.annualizedImpactEarlyPayAwarded ?? 0,
      additionalCashImpact: data.additionalImpactInstantPayAwarded ?? 0,
      dsoPercent: data.percentageDsoImpactInstantPayAwarded ?? 0,
      dsoDays: data.additionalDsoImpactInstantPayAwarded ?? 0,
    },
    totalEligibleAp: {
      earlyPayCashFlow: data.annualizedImpactEarlyPayEligible ?? 0,
      additionalCashImpact: data.additionalImpactInstantPayEligible ?? 0,
      dsoPercent: data.percentageDsoImpactInstantPayEligible ?? 0,
      dsoDays: data.additionalDsoImpactInstantPayEligible ?? 0,
    },
  };
};

export const useInstantPayCashFlowQuarterly = (): Record<CashFlowChartView, CashFlowQuarterDataPoint[]> => {
  const makerOrganizationUuids = useInstantPayMakerOrganizationUuids();
  const isValid = makerOrganizationUuids.length > 0;

  const { data } = useSuspenseQuery({
    queryKey: isValid
      ? ['instant-pay-impact-quarterly', ...makerOrganizationUuids]
      : ['instant-pay-impact-quarterly-invalid'],
    queryFn: () =>
      isValid
        ? apiClient
            .get('api/supplier-experience/instant-pay-impact/quarterly', {
              searchParams: buildInstantPayImpactSearchParams(makerOrganizationUuids),
            })
            .json<QuarterlyInstantPayImpactResponse>()
        : null,
    select: (response) => [...(response?.data ?? [])].sort((a, b) => a.yearQuarter.localeCompare(b.yearQuarter)),
  });

  return {
    awardedAp: data.map((quarter) => ({
      yearQuarter: quarter.yearQuarter,
      months: quarter.months,
      cashBenefit: quarter.instantPayBenefitAwarded ?? 0,
      dsoPercent: quarter.instantPayDsoPercentageImprovementAwarded ?? 0,
    })),
    totalEligibleAp: data.map((quarter) => ({
      yearQuarter: quarter.yearQuarter,
      months: quarter.months,
      cashBenefit: quarter.instantPayBenefitEligible ?? 0,
      dsoPercent: quarter.instantPayDsoPercentageImprovementEligible ?? 0,
    })),
  };
};
